import React, { forwardRef, useRef, useImperativeHandle, useState, useEffect } from "react";
import {
    TextInput,
    Dropdown,
    Checkbox,
    MultiSelectionInput,
    DatePicker,
} from "colbi_web_ui/lib/components/inputs";
import { useGlobalValue } from "colbi_web_ui/lib/state/GlobalProvider";
import { FORM_ACTIONS } from "../FormActions";

import styles from "./ProjectForm.module.sass";
import { checkRoles } from "colbi_web_ui/lib/utils/roleValidator";
import { UserRoles } from "colbi_web_ui/lib/utils/userRoles";
import PackageDropdownMultiSelector from "../Packages/PackageDropdownMultiSelector/PackageDropdownMultiSelector";
import { fetchApi } from "../../../config/fetchConfig";
import { checkIfProjectHasFrModule } from "../../../utils/utils";
import {
    BuildConfigurationComponentContainer,
    BuildMetadataConfigurationContainer,
} from "../../../components/Configuration/configuration";
import { connect } from "react-redux";
import { metadataToTemplate } from "../../../utils/json";
import TranslationTableAssociation from "./TranslationTableAssociation/TranslationTableAssociation";
import ThemeTab from "../../../components/Tabs/ThemeTab";

const mandatoryChars = `[!@#$%^&*()_+-=[]{};':"|,.<>/?/\\]`;

const ProjectForm = (props, ref) => {
    const {
        data,
        organizations,
        translationsTable,
        status,
        onChange,
        onSave,
        classifiers,
        projectMerge,
        projectMetadata,
        getModulesByFileType,
        dispatch,
        user: userSession,
    } = props;
    const formRef = useRef();
    const {
        id,
        description,
        taxRegistrationNumber,
        organizationId,
        active = false,
        projectModule,
        projectExpirationDate: expirationDate,
        metadata,
        delimiter,
        Packages: selectedPackages,
        fileVersion,
        translationTableAssociation: selectedTranslationTableAssociation,
    } = data || {};

    const { i18n, user } = useGlobalValue();
    const [modulesByOrganization, setModulesByOrganization] = useState([]);
    const [expirationDateProject, setExpirationDateProject] = useState(
        expirationDate ? new Date(expirationDate) : "",
    );

    const [delimiterState, setDelimiterState] = useState({});

    const [arrayConfigMerge, setArrayConfigMerge] = useState([]);
    const [configMetadata, setConfigMetadata] = useState([]);
    const [selected, setSelected] = useState(0);
    const [appliesTo, setAppliesTo] = useState([]);
    const [metadataFileTypes, setMetadataFileTypes] = useState([]);
    const [appliesToFilter, setAppliesToFilter] = useState([]);
    const [packages, setPackages] = useState([]);

    const [showFileVersionDropdown, isToShowFileVersionDropdown] = useState(false);
    const [franceFileVersionsArray, setFranceFileVersionsArray] = useState([]);
    const [franceFileVersion, setFranceFileVersion] = useState(fileVersion);

    const [metadataKeys, setMetadataKeys] = useState([]);
    const [metadataValues, setMetadataValues] = useState([]);
    const [hasTRNWarning, setTRNWarning] = useState(false);

    const [errorMetadataInput, setErrorMetadataInput] = useState(false);

    useEffect(() => {
        if (classifiers && classifiers.appliesTo && classifiers.appliesTo.length > 0) {
            setAppliesTo(classifiers.appliesTo);
        }
        if (
            classifiers &&
            classifiers.franceFileVersion &&
            classifiers.franceFileVersion.length > 0
        ) {
            setFranceFileVersionsArray(classifiers.franceFileVersion);
        }
    }, [classifiers]);

    useEffect(() => {
        if (organizationId) {
            const queriesBody = {
                query: `query($organizationId:String!){organizationPackages(organizationId:$organizationId){id,name}}`,
                variables: {
                    organizationId: `${organizationId}`,
                },
            };
            fetchApi("/graphql", queriesBody, {}, { dispatch, user: userSession })
                .then((res) => res.json())
                .then((json) => {
                    const { data } = json;
                    if (data && data.organizationPackages) {
                        setPackages(data.organizationPackages);
                    } else {
                        setPackages([]);
                    }
                })
                .catch((e) => {
                    setPackages([]);
                });
        } else {
            setPackages([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organizationId]);

    useEffect(() => {
        if (projectMerge?.length > 0) {
            setArrayConfigMerge(projectMerge);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(projectMerge)]);

    useEffect(() => {
        if (projectMetadata?.length > 0) {
            setConfigMetadata(projectMetadata);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(projectMetadata)]);

    function transformObject(obj) {
        const result = {};

        for (const key in obj) {
            const value = obj[key];
            const lowercaseKey = value.toLowerCase();

            if (!result[lowercaseKey]) {
                result[lowercaseKey] = [];
            }

            result[lowercaseKey].push(key);
        }

        return result;
    }

    useEffect(() => {
        if (projectModule && projectModule.length > 0) {
            const modulesName = modulesByOrganization.filter((module) => {
                return projectModule.includes(module.id);
            });

            const filesByModule = transformObject(JSON.parse(getModulesByFileType || "{}"));

            let currentArrayModules = [];
            for (let module of modulesName) {
                const { name } = module;
                if (filesByModule[name] && filesByModule[name].length > 0) {
                    currentArrayModules = [...currentArrayModules, ...filesByModule[name]];
                }
            }

            if (currentArrayModules && currentArrayModules.length > 0) {
                let resultAppliesToFilter = appliesTo.filter((a) => {
                    return currentArrayModules.includes(a.name.toUpperCase());
                });

                const resultConfigMerge = resultAppliesToFilter.filter((elm) => {
                    const arrayConfigMergeModules = arrayConfigMerge.map(
                        (config) => config.FileType,
                    );
                    return !arrayConfigMergeModules.includes(elm.name);
                });

                const resultConfigMetadata = resultAppliesToFilter.filter((elm) => {
                    const arrayConfigMetadataModules = configMetadata.map(
                        (config) => config.FileType,
                    );
                    return !arrayConfigMetadataModules.includes(elm.name);
                });

                setAppliesToFilter(resultConfigMerge);
                setMetadataFileTypes(resultConfigMetadata);
            }
            if (checkIfProjectHasFrModule(currentArrayModules)) {
                isToShowFileVersionDropdown(true);
            } else {
                isToShowFileVersionDropdown(false);
                setFranceFileVersion(null);
            }
        }
    }, [
        modulesByOrganization,
        projectModule,
        appliesTo,
        getModulesByFileType,
        arrayConfigMerge,
        configMetadata,
    ]);

    useEffect(() => {
        setFranceFileVersion(fileVersion);
    }, [fileVersion]);

    useEffect(() => {
        setTRNWarning(taxRegistrationNumber?.length >= 13);
    }, [taxRegistrationNumber]);

    useEffect(() => {
        try {
            if (metadata) {
                const parsedSystemMetadata = JSON.parse(metadata);
                setMetadataKeys(Object.keys(parsedSystemMetadata));
                setMetadataValues(Object.values(parsedSystemMetadata));
            }
        } catch (e) {
            alert("Invalid Json");
        }
    }, [metadata]);

    useEffect(() => {
        setExpirationDateProject(expirationDate ? new Date(expirationDate) : "");
    }, [expirationDate]);

    useEffect(() => {
        const modules = (organizations || []).find(({ id }) => id === organizationId);
        if (modules) {
            setModulesByOrganization([...(modules.organizationsModules || [])]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organizationId]);

    useEffect(() => {
        setDelimiterState(JSON.parse(delimiter || "{}"));
    }, [delimiter]);

    const handleChange = (e, prop, value) => {
        onChange &&
            onChange({
                originalEvent: e,
                target: {
                    value: {
                        data: {
                            ...data,
                            [prop]: value,
                        },
                    },
                },
            });
    };

    const handleOrganizationChange = (e, prop, value) => {
        onChange &&
            onChange({
                originalEvent: e,
                target: {
                    value: {
                        data: {
                            ...data,
                            [prop]: value,
                            projectModule: "[]",
                        },
                    },
                },
            });
    };

    useImperativeHandle(ref, () => ({
        submit: () => {
            formRef.current.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true }));
        },
    }));

    const tabs = ["Project"];

    const supportOrAdminRoles = [
        UserRoles.ADMIN,
        UserRoles.PP_SUPPORT,
        UserRoles.BACKOFFICE,
        UserRoles.SUPPORT,
    ];

    checkRoles(user, supportOrAdminRoles) &&
        tabs.push("Metadata Config", "Merge Config", "Packages", "Translation Table", "Theme");

    return status !== "loading" ? (
        <form
            ref={formRef}
            onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (formRef.current.checkValidity()) {
                    data["projectExpirationDate"] = expirationDateProject;
                    data["metadata"] = metadataToTemplate(metadataKeys, metadataValues);
                    data["delimiter"] = JSON.stringify(delimiterState);
                    data["mergeConfig"] = arrayConfigMerge;
                    data["configMetadata"] = configMetadata;
                    data["fileVersion"] = franceFileVersion || null;
                    onSave && onSave();
                } else {
                    formRef.current.reportValidity();
                }
            }}
        >
            <div className={styles["tabs"]}>
                {(tabs || []).map((tabName, index) => (
                    <div
                        key={index}
                        className={`${styles["tab"]} ${selected === index ? styles["is-selected"] : ""}`}
                        onClick={() => {
                            setSelected(index);
                        }}
                    >
                        <small>{i18n`${tabName}`}</small>
                    </div>
                ))}
            </div>
            <div className={`${styles["inputs"]} ${styles[`visible-${selected}`]}`}>
                <div>
                    <div className={styles["group-content"]}>
                        <div className={styles["flex"]}>
                            <TextInput
                                className={styles["input"]}
                                name={i18n`Tax Registration Number`}
                                placeholder={i18n`Enter a number`}
                                value={taxRegistrationNumber || ""}
                                required
                                warning={hasTRNWarning}
                                onChange={(e) => {
                                    const trn = e.target.value;
                                    setTRNWarning(trn.length >= 13);
                                    handleChange(e, "taxRegistrationNumber", trn);
                                }}
                            />
                            <TextInput
                                className={styles["input"]}
                                name={i18n`Description`}
                                placeholder={i18n`Enter a description`}
                                value={description || ""}
                                required
                                onChange={(e) => {
                                    handleChange(e, "description", e.target.value);
                                }}
                            />
                        </div>
                    </div>
                    <div className={styles["input-field"]}>
                        <h5 className={styles["label"]}>{i18n`Organization`}</h5>
                        <Dropdown
                            value={
                                organizationId && organizations
                                    ? organizations.find(({ id }) => id === organizationId) ||
                                      organizations[0]
                                    : null
                            }
                            options={organizations || []}
                            className={styles["dropdown"]}
                            placeholder={i18n`-- Select an organization --`}
                            required
                            filter
                            onChange={(e) => {
                                handleOrganizationChange(e, "organizationId", e.target.value.id);
                            }}
                        />
                    </div>
                    <div className={styles["input-field"]}>
                        <MultiSelectionInput
                            className={styles["input"]}
                            name={i18n`Modules to Current Project`}
                            value={(typeof projectModule === "string"
                                ? JSON.parse(projectModule || "[]")
                                : projectModule || []
                            )
                                .map((type) => type)
                                .filter((p) => p)}
                            placeholder={i18n`Select modules to active`}
                            options={
                                modulesByOrganization && modulesByOrganization.length
                                    ? modulesByOrganization.map(({ id, name }) => ({
                                          id,
                                          name: name,
                                      }))
                                    : []
                            }
                            onChange={(e) => {
                                handleChange(e, "projectModule", JSON.stringify(e.target.value));
                            }}
                        />
                        <BuildMetadataConfigurationContainer
                            uniqueName={"userMetadata"}
                            keysArray={metadataKeys}
                            valuesArray={metadataValues}
                            keysSetter={setMetadataKeys}
                            valuesSetter={setMetadataValues}
                            addButtonMessage={"[+] Add user metadata configuration"}
                            noMetadataConfigsMessage={"No metadata configurations assigned"}
                            containerTitle={"User Metadata"}
                            keyErrorCheck={errorMetadataInput}
                            keyErrorSetter={setErrorMetadataInput}
                            dupKeyMessage={
                                "Key on User Metadata can't have duplicate column values"
                            }
                        />
                        {showFileVersionDropdown ? (
                            <div>
                                <h5 className={styles["label"]}>{i18n`File Version`}</h5>
                                <Dropdown
                                    className={styles["dropdown"]}
                                    placeholder={i18n`-- Select an option --`}
                                    value={
                                        franceFileVersion && franceFileVersionsArray
                                            ? franceFileVersionsArray.find(
                                                  ({ id }) => id === franceFileVersion,
                                              ) || null
                                            : null
                                    }
                                    options={franceFileVersionsArray}
                                    onChange={(e) => {
                                        setFranceFileVersion(e.target.value.id);
                                    }}
                                />
                            </div>
                        ) : null}
                        <div>
                            <DatePicker
                                className={`${styles["wrap-datepicker"]} ${styles["input"]}`}
                                name={i18n`Expiration Date`}
                                value={expirationDateProject ? expirationDateProject : ""}
                                onChange={(e) => {
                                    setExpirationDateProject(e);
                                }}
                                dateFormat="dd-MM-yyyy"
                            />
                        </div>
                    </div>
                    <div className={styles["group-content"]}>
                        <div className={styles["flex"]}>
                            {checkRoles(user, supportOrAdminRoles) ? (
                                <TextInput
                                    className={`${styles["input"]}`}
                                    name={i18n`XML Delimiter`}
                                    placeholder={i18n`Enter a XML delimiter`}
                                    value={
                                        delimiterState && delimiterState.xmlDelimiter
                                            ? delimiterState.xmlDelimiter
                                            : ""
                                    }
                                    maxLength="3"
                                    onChange={(e) => {
                                        setDelimiterState({
                                            ...delimiterState,
                                            xmlDelimiter: e.target.value,
                                        });
                                    }}
                                />
                            ) : null}
                            <TextInput
                                className={`${styles["input"]}`}
                                name={i18n`Source Data Delimiter`}
                                placeholder={i18n`Enter a Source Data delimiter`}
                                iconPlaceholder={i18n`The delimiter must be one of these characters: ${mandatoryChars}`}
                                value={
                                    delimiterState && delimiterState.csvDelimiter
                                        ? delimiterState.csvDelimiter
                                        : ""
                                }
                                maxLength="3"
                                pattern=".*[!@#$%^&*()_+\-=\[\]{};':&quot;\\|,.<>\/?]+.*"
                                onChange={(e) => {
                                    setDelimiterState({
                                        ...delimiterState,
                                        csvDelimiter: e.target.value,
                                    });
                                }}
                            />
                        </div>
                    </div>
                    <Checkbox
                        style={{ marginTop: "10px" }}
                        value={active}
                        onChange={(e) => {
                            handleChange(e, "active", e.target.checked ? 1 : 0);
                        }}
                    >{i18n`Active`}</Checkbox>
                </div>
                {checkRoles(user, supportOrAdminRoles) ? (
                    <>
                        <div>
                            <div className={styles["group-content"]}>
                                <BuildConfigurationComponentContainer
                                    fileType={metadataFileTypes}
                                    target={configMetadata}
                                    setter={setConfigMetadata}
                                />
                            </div>
                        </div>
                        <div>
                            <div className={styles["group-content"]} name={appliesToFilter.length}>
                                <BuildConfigurationComponentContainer
                                    fileType={appliesToFilter}
                                    target={arrayConfigMerge}
                                    setter={setArrayConfigMerge}
                                />
                            </div>
                        </div>

                        <PackageDropdownMultiSelector
                            packages={packages}
                            selectedPackages={selectedPackages}
                            handleChange={handleChange}
                            entitieID={id}
                            containerTitle={"Packages"}
                            entitieKey={"projectId"}
                        />

                        <TranslationTableAssociation
                            selectedTranslationTableAssociation={
                                selectedTranslationTableAssociation
                            }
                            translationsTable={translationsTable}
                            handleChange={handleChange}
                            containerTitle={"Translation Table"}
                            projectId={+id}
                        />
                        <ThemeTab data={data} handleChange={handleChange} styles={styles} />
                    </>
                ) : null}
            </div>
        </form>
    ) : null;
};

const ProjectFormRef = forwardRef(ProjectForm);

ProjectFormRef.queries = (args) => {
    return [
        {
            resource: "getModulesByFileType",
        },
        {
            resource: "projectMerge",
            args: { targetProjectId: args.id },
            body: `
                    ID,        
                    FileType
                    Description
                    Configuration
                    `,
        },
        {
            resource: "projectMetadata",
            args: { projectId: args.id },
            body: `
                    ID,        
                    FileType
                    Description
                    Configuration
                    `,
        },
        {
            resource: "classifiers",
            body: `
                severity{id, name},
                level{id, name},
                referenceType{id, name},
                appliesToDataType{id, name},
                appliesTo{id, name},
                appliesToVersion{id, name},
                franceFileVersion{id, name}
            `,
        },
        {
            resource: "user_projects_organizations",
            body: "id,name,organizationsModules{id, name}",
        },
        args && args.id
            ? {
                  resource: "project_organization_translations_table",
                  args: { projectId: args.id },
                  body: "id, name, ",
              }
            : undefined,
        args && args.id
            ? {
                  resource: "project",
                  args: { targetProjectId: args.id },
                  body: "__typename,id,description,taxRegistrationNumber,organizationId,active,projectModule,projectExpirationDate,metadata,delimiter,Packages{id, packageId, projectId},fileVersion, translationTableAssociation{id, projectId,translationId}, mosaicThemeConfig",
              }
            : undefined,
    ].filter((q) => q);
};

ProjectFormRef.dataMapper = [
    "getModulesByFileType",
    "projectMerge",
    "projectMetadata",
    "classifiers",
    "organizations",
    "translationsTable",
    "data",
];

ProjectFormRef.actions = [FORM_ACTIONS.SAVE];

export default connect(
    ({ user }) => ({
        user,
    }),
    null,
    null,
    { forwardRef: true },
)(ProjectFormRef);
